// src/shared/components/dashboard/KpiCell.tsx
// ────────────────────────────────────────────────────────────────────────────
// Universal KPI cell — single source of truth for all KPI rendering across
// SPA dashboards, agent-chat cards, and any future surface.
//
// Unifies: ArchiverDashboard.StatCard, AgentChatTelemetryCard.KpiBox,
// LegalDashboard.StatCard — one component, one contract.
// ────────────────────────────────────────────────────────────────────────────
import React, { memo } from 'react';

export interface KpiCellProps {
  /** KPI label (uppercase, small) */
  label: string;
  /** Primary value (number, string, formatted) */
  value: string | number;
  /** Optional sub-label (secondary info) */
  sub?: string;
  /** Tailwind text color for the value — default: text-neutral-100 */
  accent?: string;
  /** Size variant: 'sm' for compact (agent-chat cards), 'md' for dashboards */
  size?: 'sm' | 'md';
}

function KpiCell({ label, value, sub, accent = 'text-neutral-100', size = 'md' }: KpiCellProps) {
  const isSmall = size === 'sm';
  return (
    <div className={`text-center min-w-0 ${isSmall ? 'px-2 py-2' : 'px-3 py-3'}`}>
      <p className={`font-bold tabular-nums leading-tight ${accent} ${isSmall ? 'text-lg' : 'text-2xl'}`}>
        {value}
      </p>
      <p className={`uppercase tracking-wider text-neutral-500 mt-0.5 truncate ${isSmall ? 'text-[10px]' : 'text-[10px]'}`}>
        {label}
      </p>
      {sub && (
        <p className={`text-neutral-600 mt-0.5 ${isSmall ? 'text-[9px]' : 'text-[11px]'}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

export default memo(KpiCell);
